/* One date formatter for the two admin apps.
 *
 * THREE THINGS THE AUDIT FOUND, ALL FIXED HERE
 * --------------------------------------------
 * 1. The console shows ``2026-08-27T19:29:14.622193Z`` — a raw ISO
 *    timestamp with microseconds — in the main column of Anuncios and
 *    Auditoría.
 * 2. The builder's report list mixes ``22 jul 2026`` and ``hace 26 días``
 *    inside the SAME column, so two rows that look different may be a day
 *    apart.
 * 3. Nothing says what happens when the value will not parse.
 *
 * The contract: a column picks ``absolute`` or ``relative`` ONCE, and an
 * unparseable value is reported as unparseable rather than replaced by a
 * dash. Inventing "—" for a value the backend actually sent would hide a
 * real defect, and this codebase does not do that.
 */

import type { ReactNode } from "react";

import { cx } from "./layout";

export type DateMode = "absolute" | "absoluteWithTime" | "relative";

export interface FormattedDate {
  text: string;
  /** True when the input could not be parsed as a date. ``text`` is then
   *  the raw input, untouched. */
  invalid: boolean;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Formats an ISO string (or Date, or epoch ms) for display.
 *
 * ``locale`` is a BCP-47 tag the caller already knows — the two apps
 * both track the active language, so this package does not have to.
 */
export function formatDate(
  value: string | number | Date | null | undefined,
  {
    locale,
    mode = "absolute",
    now = Date.now(),
  }: { locale: string; mode?: DateMode; now?: number },
): FormattedDate {
  if (value === null || value === undefined || value === "") {
    return { text: "—", invalid: false };
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { text: String(value), invalid: true };
  }

  if (mode === "relative") {
    const diff = date.getTime() - now;
    const abs = Math.abs(diff);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (abs < HOUR) {
      return { text: rtf.format(Math.round(diff / MINUTE), "minute"), invalid: false };
    }
    if (abs < DAY) {
      return { text: rtf.format(Math.round(diff / HOUR), "hour"), invalid: false };
    }
    if (abs < 30 * DAY) {
      return { text: rtf.format(Math.round(diff / DAY), "day"), invalid: false };
    }
    // Past a month, "hace 3 meses" is less useful than the date itself.
    return {
      text: new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(date),
      invalid: false,
    };
  }

  const options: Intl.DateTimeFormatOptions =
    mode === "absoluteWithTime"
      ? {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      : { day: "numeric", month: "short", year: "numeric" };

  return { text: new Intl.DateTimeFormat(locale, options).format(date), invalid: false };
}

export interface DateCellProps {
  value: string | number | Date | null | undefined;
  locale: string;
  mode?: DateMode;
  /** Tooltip shown when the value cannot be parsed — the caller
   *  translates it. */
  invalidHint?: string;
  className?: string;
}

/**
 * Renders a date in a table cell. An unparseable value shows the raw
 * input in monospace with a tooltip, so a bad timestamp looks like a bug
 * instead of looking like data.
 */
export function DateCell({
  value,
  locale,
  mode = "absolute",
  invalidHint,
  className,
}: DateCellProps): ReactNode {
  const { text, invalid } = formatDate(value, { locale, mode });
  return (
    <span
      title={invalid ? invalidHint : undefined}
      className={cx(
        invalid ? "font-mono text-xs text-warning-dark" : "text-neutral-700",
        className,
      )}
    >
      {text}
    </span>
  );
}
